import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { Box, IconButton, Skeleton, Stack } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  AlertCircle,
  CheckCircle2,
  ChevronLeft,
  Globe,
  Kanban,
  Megaphone,
  PlayCircle,
  PenTool,
  Rocket,
  Settings,
  Share2,
  Target,
  Zap,
} from "lucide-react";

import { useAuth } from "../../components/contexts/AuthContext";
import { getData } from "../../components/hooks/getData";
import { patchWithRefresh } from "../../api-client";
import { Button } from "../../components/Button";
import { Badge } from "../../components/Badge";
import SuccessPopup from "../../components/SuccessPopup";
import { EmptyStateContainer } from "../../components/EmptyStateContainer";
import { SetupDetailView } from "../TechnicalSetup/SetupDetailView";
import { EditTaskModal } from "../TechnicalSetup/EditTaskModal";
import { KanbanView } from "./components/KanbanView";
import { PROJECT_STATE_BADGES } from "./stateBadges";
import { Card, CardContent } from "@/components/Card";
import { ConfirmModal } from "../../components/ConfirmModal";
import { isClientReadOnly } from "@/utils/clientAccess";

const phaseIcons = [Target, Megaphone, Globe, Share2, PenTool, Zap, Rocket];

const COMPLETED_TASK_STATUSES = new Set(["finalizado", "completed"]);
const EXCLUDED_PROGRESS_STATUSES = new Set(["no_necesario"]);

const getPhaseIcon = (order) => phaseIcons[order - 1] || Settings;

const getTaskIcon = (task) => {
  if (task?.priority === "high") {
    return AlertCircle;
  }
  return Settings;
};

const mapPhase = (phase) => ({
  id: phase.id,
  title: phase.name,
  description: phase.description || "",
  status: phase.state || "not_started",
  icon: getPhaseIcon(phase.order),
  progress: Math.round(phase.progress_percentage || 0),
  order: phase.order,
  only_tasks: phase.only_tasks || false,
  tasks: (phase.tasks || []).map((task) => ({
    id: task.id,
    title: task.name,
    description: task.description,
    status: task.state || "planeado",
    icon: getTaskIcon(task),
    deadline: task.deadline,
    assigned_to: task.assigned_to,
    assigned_to_name: task.assigned_to_name,
    priority: task.priority || "medium",
    documentation_urls: task.documentation_urls || [],
    google_calendar_link: task.google_calendar_link || "",
  })),
});

const mapPhaseStatusToKanban = (status) => {
  switch (status) {
    case "in_progress":
      return "in_progress";
    case "completed":
      return "completed";
    case "no_necesario":
      return "no_necesario";
    default:
      return "not_started";
  }
};

const mapTaskStatusToKanban = (status) => {
  switch (status) {
    case "en_desarrollo":
      return "in_progress";
    case "finalizado":
      return "completed";
    case "no_necesario":
      return "no_necesario";
    default:
      return "not_started";
  }
};

const mapKanbanStatusToTaskState = (status) => {
  switch (status) {
    case "in_progress":
      return "en_desarrollo";
    case "completed":
      return "finalizado";
    case "no_necesario":
      return "no_necesario";
    default:
      return "planeado";
  }
};

const mapProjectStateToKanban = (status) => {
  switch (status) {
    case "completed":
      return "completed";
    case "planning":
    case "not_started":
      return "planning";
    case "cancelled":
      return "planning";
    default:
      return "in-progress";
  }
};

const mapProjectTypeToRouteSegment = (projectType) => {
  if (projectType === "recurrent_monthly") {
    return "recurrent";
  }
  return "custom";
};

export function ProjectDetail() {
  const { projectType, projectId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { user: currentUser, interceptorReady } = useAuth();
  const { setPageContext, projectsRefreshKey, selectedFilterData } =
    useOutletContext();
  const projectsBasePath = useMemo(() => {
    if (projectType === "recurrent") {
      return "/projects/recurrent";
    }
    return "/projects/custom";
  }, [projectType]);

  const [phases, setPhases] = useState([]);
  const [taskToEdit, setTaskToEdit] = useState(null);
  const [isPhaseEdit, setIsPhaseEdit] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [isError, setIsError] = useState(false);
  const [showKanban, setShowKanban] = useState(false);
  const [showPhaseKanban, setShowPhaseKanban] = useState(false);
  const [selectedPhaseId, setSelectedPhaseId] = useState(null);
  const [localPhaseTasks, setLocalPhaseTasks] = useState([]);
  const [draggedPhase, setDraggedPhase] = useState(null);
  const [draggedPhaseTask, setDraggedPhaseTask] = useState(null);
  const [isModalFromKanban, setIsModalFromKanban] = useState(false);
  const [phaseTasksRefreshTrigger, setPhaseTasksRefreshTrigger] = useState(0); // Para forzar refetch de tareas de fase
  const [manualProjectRefresh, setManualProjectRefresh] = useState(0); // Solo para refreshes manuales (kanban, etc)
  const [projectProgress, setProjectProgress] = useState(0);
  const [detailProjectState, setDetailProjectState] = useState(null);
  const [stateConfirmOpen, setStateConfirmOpen] = useState(false);
  const [pendingStateChange, setPendingStateChange] = useState(null);

  const canEditTasks = useMemo(
    () =>
      currentUser?.role === "agency_admin" ||
      currentUser?.role === "consultant" ||
      currentUser?.role === "superadmin",
    [currentUser?.role]
  );
  const isClientUser = currentUser?.role === "client";
  const isAgencyConsultantOrSuperadmin = [
    "agency_admin",
    "consultant",
    "superadmin",
  ].includes(currentUser?.role);
  const prevSelectedFilterId = useRef(selectedFilterData?.id);

  // Detectar si el proyecto es de solo tareas
  const isOnlyTasksMode = useMemo(() => {
    return phases.length > 0 && phases[0].only_tasks;
  }, [phases]);

  // Navegar de vuelta a /projects si cambia el selectedFilter
  useEffect(() => {
    if (currentUser?.role === "client" && projectType === "recurrent") {
      navigate("/projects/custom", { replace: true });
      return;
    }

    if (!isAgencyConsultantOrSuperadmin) {
      prevSelectedFilterId.current = selectedFilterData?.id;
      return;
    }

    const previousId = prevSelectedFilterId.current;
    const currentId = selectedFilterData?.id;

    if (
      previousId !== undefined &&
      previousId !== null &&
      previousId !== currentId
    ) {
      navigate(projectsBasePath);
    }

    prevSelectedFilterId.current = currentId;
  }, [
    isAgencyConsultantOrSuperadmin,
    currentUser?.role,
    navigate,
    projectsBasePath,
    projectType,
    selectedFilterData?.id,
  ]);

  const {
    data: projectData,
    isLoading,
    error: projectError,
  } = getData(
    projectId ? `projects/projects/${projectId}` : null,
    Boolean(projectId) && interceptorReady,
    [manualProjectRefresh, projectsRefreshKey]
  );

  useEffect(() => {
    if (!projectData?.phases) return;

    const mapped = projectData.phases
      .map(mapPhase)
      .sort((a, b) => a.order - b.order);

    setPhases(mapped);
  }, [projectData]);

  const projectClientId = projectData?.client || null;
  const isReadOnlyClient = isClientReadOnly(currentUser, projectClientId);
  const canEditProject = canEditTasks && !isReadOnlyClient;

  const calculateProjectProgress = useCallback((phasesToEvaluate) => {
    if (!Array.isArray(phasesToEvaluate) || phasesToEvaluate.length === 0) {
      return null;
    }

    const validTasks = phasesToEvaluate.flatMap((phase) =>
      (phase.tasks || []).filter(
        (task) => task && !EXCLUDED_PROGRESS_STATUSES.has(task.status)
      )
    );

    if (!validTasks.length) {
      return null;
    }

    const completedTasks = validTasks.filter((task) =>
      COMPLETED_TASK_STATUSES.has(task.status)
    ).length;

    return Math.round((completedTasks / validTasks.length) * 100);
  }, []);

  const { data: clientData } = getData(
    projectClientId && !isClientUser ? `clients/${projectClientId}` : null,
    Boolean(projectClientId) && !isClientUser && interceptorReady,
    [projectClientId, projectsRefreshKey]
  );

  // Cargar tareas de la fase seleccionada para el kanban
  const { data: selectedPhaseTasksData, error: selectedPhaseTasksError } =
    getData(
      selectedPhaseId ? `projects/tasks/?phase=${selectedPhaseId}` : null,
      !!selectedPhaseId && interceptorReady,
      [selectedPhaseId, phaseTasksRefreshTrigger, projectsRefreshKey]
    );

  useEffect(() => {
    if (!selectedPhaseId) {
      setLocalPhaseTasks([]);
      return;
    }

    // Si tenemos datos frescos del endpoint de tareas, usarlos
    if (selectedPhaseTasksData?.results) {
      const mappedTasks = selectedPhaseTasksData.results.map((task) => ({
        id: task.id,
        title: task.name,
        status: mapTaskStatusToKanban(task.state || "planeado"),
        priority: task.priority || "medium",
        assignee: task.assigned_to_name || "Sin asignar",
        assigned_to: task.assigned_to,
        assigned_to_name: task.assigned_to_name,
        dueDate: task.deadline,
        description: task.description,
        documentation_urls: task.documentation_urls || [],
        google_calendar_link: task.google_calendar_link || "",
      }));
      setLocalPhaseTasks(mappedTasks);
      return;
    }

    // Fallback: usar las tareas que vienen con las fases del proyecto
    const currentPhase = phases.find((phase) => phase.id === selectedPhaseId);

    if (!currentPhase || !currentPhase.tasks) {
      setLocalPhaseTasks([]);
      return;
    }

    const mappedTasks = currentPhase.tasks.map((task) => ({
      id: task.id,
      title: task.title,
      status: mapTaskStatusToKanban(task.status),
      priority: task.priority || "medium",
      assignee: task.assigned_to_name || "Sin asignar",
      assigned_to: task.assigned_to,
      assigned_to_name: task.assigned_to_name,
      dueDate: task.deadline,
      description: task.description,
      documentation_urls: task.documentation_urls || [],
      google_calendar_link: task.google_calendar_link || "",
    }));

    setLocalPhaseTasks(mappedTasks);
  }, [selectedPhaseId, phases, selectedPhaseTasksData]);

  useEffect(() => {
    if (showPhaseKanban && selectedPhaseId) {
      const exists = phases.some((phase) => phase.id === selectedPhaseId);
      if (!exists) {
        handleClosePhaseKanban();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phases, showPhaseKanban, selectedPhaseId]);

  useEffect(() => {
    if (!projectData) {
      setProjectProgress(0);
      return;
    }

    setProjectProgress(Math.round(projectData.progress_percentage || 0));
  }, [projectData]);

  useEffect(() => {
    const localProgress = calculateProjectProgress(phases);

    if (localProgress !== null) {
      setProjectProgress(localProgress);
    }
  }, [phases, calculateProjectProgress]);

  useEffect(() => {
    if (!projectData?.state) {
      setDetailProjectState(null);
      return;
    }

    setDetailProjectState(projectData.state);
  }, [projectData?.state]);

  const projectState = detailProjectState || projectData?.state || "";
  const selectedPhase = useMemo(
    () => phases.find((phase) => phase.id === selectedPhaseId) || null,
    [phases, selectedPhaseId]
  );

  const projectTitle = projectData?.name || "Proyecto";
  const clientName = projectData?.client_name || "";
  const kanbanProjectInfo = {
    id: projectData?.id ?? projectId,
    name: projectTitle,
    clientName,
    status: mapProjectStateToKanban(projectState),
  };
  const availableConsultants = useMemo(() => {
    // Combinar consultant_details y admin_details de clientData
    if (clientData?.consultant_details || clientData?.admin_details) {
      const consultants = clientData.consultant_details || [];
      const adminDetailsRaw = clientData.admin_details;

      // Normalizar admin_details a array (puede venir como objeto o array)
      let admins = [];
      if (adminDetailsRaw) {
        if (Array.isArray(adminDetailsRaw)) {
          admins = adminDetailsRaw;
        } else {
          admins = [adminDetailsRaw];
        }
      }

      // Combinar admins primero, luego consultores
      return [
        ...admins.map((admin) => ({
          id: admin.id,
          full_name: admin.full_name,
          email: admin.email,
          role: admin.role,
          profile_image: admin.profile_image,
        })),
        ...consultants.map((consultant) => ({
          id: consultant.id,
          full_name: consultant.full_name,
          email: consultant.email,
          role: consultant.role,
          profile_image: consultant.profile_image,
        })),
      ];
    }

    // Fallback a projectData.consultants si no hay clientData
    if (projectData?.consultants?.length) {
      return projectData.consultants.map((consultant) => ({
        id: consultant.id,
        full_name:
          consultant.full_name ||
          consultant.name ||
          `${consultant.first_name || ""} ${consultant.last_name || ""}`.trim(),
        email: consultant.email,
        role: consultant.role,
        profile_image: consultant.profile_image,
      }));
    }

    return [];
  }, [
    clientData?.consultant_details,
    clientData?.admin_details,
    projectData?.consultants,
  ]);

  const runProjectStateChange = useCallback(
    async (newState) => {
      if (!projectId) return;

      try {
        await patchWithRefresh(`projects/projects/${projectId}/`, {
          state: newState,
        });
        setDetailProjectState(newState);
        setPopupMessage(`Proyecto marcado como ${PROJECT_STATE_BADGES[newState].label}`);
        setIsError(false);
        setPopupOpen(true);
      } catch (error) {
        console.error("Error al cambiar estado del proyecto:", error);
        setPopupMessage(
          error?.response?.data?.detail ||
            error?.response?.data?.message ||
            "No se pudo actualizar el estado del proyecto. Intenta nuevamente."
        );
        setIsError(true);
        setPopupOpen(true);
      }
    },
    [projectId]
  );

  const requestProjectStateChange = useCallback(
    (newState) => {
      if (newState === "completed") {
        setPendingStateChange(newState);
        setStateConfirmOpen(true);
        return;
      }

      runProjectStateChange(newState);
    },
    [runProjectStateChange]
  );

  const handleConfirmProjectStateChange = useCallback(async () => {
    if (!pendingStateChange) return;
    const nextState = pendingStateChange;
    setStateConfirmOpen(false);
    setPendingStateChange(null);
    await runProjectStateChange(nextState);
  }, [pendingStateChange, runProjectStateChange]);

  const handleDetailProjectStateAction = useCallback(() => {
    if (projectState === "not_started") {
      requestProjectStateChange("in_progress");
      return;
    }

    if (projectState === "in_progress") {
      requestProjectStateChange("completed");
    }
  }, [projectState, requestProjectStateChange]);

  useEffect(() => {
    const backPath = isClientUser
      ? "custom"
      : projectData?.project_type
        ? mapProjectTypeToRouteSegment(projectData.project_type)
        : projectType === "recurrent"
          ? "recurrent"
          : "custom";
    const resolvedProjectsBasePath = `/projects/${backPath}`;

    const leftContent = (
      <IconButton
        onClick={() => navigate(resolvedProjectsBasePath)}
        size="small"
        sx={{
          color: "primary.main",
          padding: "4px",
          "&:hover": { backgroundColor: "action.hover" },
        }}
        disableRipple={false}
      >
        <ChevronLeft size={20} />
      </IconButton>
    );

    const breadcrumbItems = (() => {
      const goToProjects = () => navigate(resolvedProjectsBasePath);
      const items = [
        {
          label: "Vista Global",
          onClick: goToProjects,
        },
      ];

      if (isAgencyConsultantOrSuperadmin) {
        if (selectedFilterData?.id && selectedFilterData.id !== "global") {
          items.push({ label: selectedFilterData.name, onClick: goToProjects });
        }
        items.push({ label: `Proyecto: "${projectTitle}"` });
        return items;
      }

      if (selectedFilterData && selectedFilterData.name !== "Vista Global") {
        items.push({ label: selectedFilterData.name, onClick: goToProjects });
      }
      items.push({ label: `Proyecto: ${projectTitle}` });
      return items;
    })();

    const canShowDetailStateAction =
      canEditProject &&
      (projectState === "not_started" || projectState === "in_progress");
    const stateContent =
      projectState === "completed" ? (
        <Badge color="green" size="lg">
          Completado
        </Badge>
      ) : canShowDetailStateAction ? (
        <Button
          size="sm"
          onClick={handleDetailProjectStateAction}
          sx={{ whiteSpace: "nowrap" }}
        >
          {projectState === "not_started" ? (
            <>
              <PlayCircle size={14} style={{ marginRight: "8px" }} />
              Marcar en progreso
            </>
          ) : (
            <>
              <CheckCircle2 size={14} style={{ marginRight: "8px" }} />
              Marcar como completado
            </>
          )}
        </Button>
      ) : null;

    // Como en el detalle del setup, el acceso al kanban va junto a la acción de estado
    const rightContent = (
      <>
        {phases.length > 0 ? (
          <Button variant="dark" size="sm" onClick={() => handleViewKanban()}>
            <Kanban size={14} style={{ marginRight: "8px" }} />
            Ver en kanban
          </Button>
        ) : null}
        {stateContent}
      </>
    );

    setPageContext({
      title: (
        <>
          Proyecto:{"  "}
          <span
            style={{ color: theme.palette.text.secondary, marginLeft: "4px" }}
          >
            {projectTitle}
          </span>
        </>
      ),
      subtitle: clientName ? `Cliente: ${clientName}` : undefined,
      showTitle: true,
      leftContent,
      rightContent,
      breadcrumbItems,
    });
  }, [
    projectTitle,
    clientName,
    canEditProject,
    projectState,
    handleDetailProjectStateAction,
    navigate,
    setPageContext,
    theme.palette.text.secondary,
    selectedFilterData,
    isAgencyConsultantOrSuperadmin,
    isClientUser,
    projectData?.project_type,
    projectType,
    phases,
    isOnlyTasksMode,
  ]);

  const showPopup = (message, error = false) => {
    setPopupMessage(message);
    setIsError(error);
    setPopupOpen(true);
  };

  const mapBackendTaskToLocal = (task) => ({
    id: task.id,
    title: task.title || task.name || "",
    description: task.description || "",
    status: task.status || task.state || "planeado",
    priority: task.priority || "medium",
    deadline: task.deadline,
    dueDate: task.deadline || task.dueDate,
    assigned_to: task.assigned_to,
    assigned_to_name: task.assigned_to_name || task.assignee,
    documentation_urls: task.documentation_urls || [],
    google_calendar_link: task.google_calendar_link || "",
  });

  const mapKanbanTaskToLocal = (task) => ({
    ...mapBackendTaskToLocal(task),
    status: mapKanbanStatusToTaskState(
      task.status || task.state || "not_started"
    ),
  });

  const getPhaseTasksWithLatestData = (phaseId) => {
    const localPhase = phases.find((item) => item.id === phaseId);
    if (localPhase?.tasks?.length) {
      return localPhase.tasks;
    }

    if (selectedPhaseId === phaseId && localPhaseTasks.length) {
      return localPhaseTasks.map((task) => mapKanbanTaskToLocal(task));
    }

    const projectPhase = projectData?.phases?.find(
      (item) => item.id === phaseId
    );
    if (projectPhase?.tasks?.length) {
      return projectPhase.tasks.map((task) => mapBackendTaskToLocal(task));
    }

    return [];
  };

  const findTaskWithLatestData = (taskId) => {
    if (!taskId) return null;

    const phaseWithTask = phases.find((phase) =>
      phase.tasks?.some((task) => task.id === taskId)
    );
    if (phaseWithTask) {
      return phaseWithTask.tasks.find((task) => task.id === taskId);
    }

    const kanbanTask = localPhaseTasks.find((task) => task.id === taskId);
    if (kanbanTask) {
      return mapKanbanTaskToLocal(kanbanTask);
    }

    if (projectData?.phases) {
      for (const phase of projectData.phases) {
        const task = phase.tasks?.find((item) => item.id === taskId);
        if (task) {
          return mapBackendTaskToLocal(task);
        }
      }
    }

    return null;
  };

  const updatePhaseTaskStatusLocally = (taskId, newStatus) => {
    setLocalPhaseTasks((prev) =>
      prev.map((task) =>
        task.id === taskId ? { ...task, status: newStatus } : task
      )
    );
  };

  const getPhasesForKanban = () =>
    phases.map((phase, index) => {
      const validTasks = (phase.tasks || []).filter(
        (task) => task.status !== "no_necesario"
      );
      const completedTasks = validTasks.filter(
        (task) => task.status === "finalizado"
      ).length;

      return {
        id: phase.id,
        title: `${index + 1}. ${phase.title}`,
        status: mapPhaseStatusToKanban(phase.status),
        tasksCount: validTasks.length,
        completedTasksCount: completedTasks,
        progress: phase.progress,
        icon: phase.icon,
        phaseNumber: index + 1,
      };
    });

  const getPhaseTasksForKanban = () => localPhaseTasks;

  const handleViewKanban = () => {
    if (!phases.length) return;

    if (isOnlyTasksMode) {
      handleViewPhaseKanban(phases[0].id);
      return;
    }

    setManualProjectRefresh((prev) => prev + 1); // Forzar refetch del proyecto
    setShowKanban(true);
  };

  const handleCloseKanban = () => {
    setManualProjectRefresh((prev) => prev + 1); // Refresh del proyecto al cerrar kanban
    setShowKanban(false);
  };

  const handleViewPhaseKanban = (phaseId) => {
    setSelectedPhaseId(phaseId);
    setPhaseTasksRefreshTrigger((prev) => prev + 1); // Forzar refresh de tareas de fase
    setShowPhaseKanban(true);
  };

  const handleClosePhaseKanban = () => {
    setManualProjectRefresh((prev) => prev + 1); // Refresh del proyecto al cerrar kanban de fase
    setShowPhaseKanban(false);
    setSelectedPhaseId(null);
    setLocalPhaseTasks([]);
  };

  const handlePhaseDragStart = (event, phase) => {
    setDraggedPhase(phase);
    event.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  };

  const handlePhaseDrop = (event, newStatus) => {
    event.preventDefault();
    if (draggedPhase && draggedPhase.status !== newStatus) {
      updatePhaseStatus(draggedPhase.id, newStatus);
    }
    setDraggedPhase(null);
  };

  const handlePhaseTaskDragStart = (event, task) => {
    setDraggedPhaseTask(task);
    event.dataTransfer.effectAllowed = "move";
  };

  const handlePhaseTaskDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  };

  const handlePhaseTaskDrop = async (event, newKanbanStatus) => {
    event.preventDefault();
    if (!selectedPhaseId || !draggedPhaseTask) {
      return;
    }

    if (draggedPhaseTask.status !== newKanbanStatus) {
      const backendStatus = mapKanbanStatusToTaskState(newKanbanStatus);
      const success = await updateTaskStatus(
        selectedPhaseId,
        draggedPhaseTask.id,
        backendStatus
      );

      if (!success) {
        updatePhaseTaskStatusLocally(
          draggedPhaseTask.id,
          draggedPhaseTask.status
        );
      }
    }

    setDraggedPhaseTask(null);
  };

  const updatePhaseStatus = async (phaseId, newStatus) => {
    const currentPhase = phases.find((phase) => phase.id === phaseId);

    if (!currentPhase || currentPhase.status === newStatus) {
      return;
    }

    setPhases((prev) =>
      prev.map((phase) =>
        phase.id === phaseId ? { ...phase, status: newStatus } : phase
      )
    );

    try {
      await patchWithRefresh(`projects/phases/${phaseId}/`, {
        state: newStatus,
      });

      // Si la fase se marcó como completada, actualizar todas las tareas localmente
      // (excepto las que ya están 'finalizado')
      // También actualizar el progreso de la fase al 100%
      if (newStatus === "completed") {
        setPhases((prev) =>
          prev.map((phase) => {
            if (phase.id !== phaseId) return phase;

            return {
              ...phase,
              progress: 100, // Actualizar progreso al 100%
              tasks: phase.tasks.map((task) => {
                // No modificar tareas que ya están finalizadas
                if (task.status === "finalizado") {
                  return task;
                }
                // Marcar como finalizado
                return { ...task, status: "finalizado" };
              }),
            };
          })
        );
      }

      // Si la fase se marcó como no necesaria, marcar TODAS las tareas localmente
      if (newStatus === "no_necesario") {
        setPhases((prev) =>
          prev.map((phase) => {
            if (phase.id !== phaseId) return phase;

            return {
              ...phase,
              tasks: phase.tasks.map((task) => ({ ...task, status: "no_necesario" })),
            };
          })
        );
      }

      // Si la fase se marcó como en progreso, marcar TODAS las tareas como en desarrollo
      if (newStatus === "in_progress") {
        setPhases((prev) =>
          prev.map((phase) => {
            if (phase.id !== phaseId) return phase;

            return {
              ...phase,
              progress: 0,
              tasks: phase.tasks.map((task) => ({
                ...task,
                status: "en_desarrollo",
              })),
            };
          })
        );
      }

      // Si la fase se marcó como pendiente, marcar TODAS las tareas como planeado
      if (newStatus === "not_started") {
        setPhases((prev) =>
          prev.map((phase) => {
            if (phase.id !== phaseId) return phase;

            return {
              ...phase,
              progress: 0,
              tasks: phase.tasks.map((task) => ({ ...task, status: "planeado" })),
            };
          })
        );
      }

      showPopup("Estado de la fase actualizado correctamente");
    } catch (error) {
      console.error("Error al actualizar fase", error);
      setPhases((prev) =>
        prev.map((phase) =>
          phase.id === phaseId
            ? { ...phase, status: currentPhase.status }
            : phase
        )
      );
      showPopup(
        "No se pudo actualizar el estado de la fase. Intenta nuevamente.",
        true
      );
    }
  };

  const updateTaskStatus = async (phaseId, taskId, newStatus) => {
    const phase = phases.find((item) => item.id === phaseId);
    const task = phase?.tasks?.find((item) => item.id === taskId);

    if (!phase || !task || task.status === newStatus) {
      return true;
    }

    setPhases((prev) =>
      prev.map((item) =>
        item.id === phaseId
          ? {
            ...item,
            tasks: item.tasks.map((taskItem) =>
              taskItem.id === taskId
                ? { ...taskItem, status: newStatus }
                : taskItem
            ),
          }
          : item
      )
    );

    try {
      await patchWithRefresh(`projects/tasks/${taskId}/update-state/`, {
        state: newStatus,
      });
      showPopup("Estado de la tarea actualizado correctamente");
      return true;
    } catch (error) {
      console.error("Error al actualizar tarea", error);
      setPhases((prev) =>
        prev.map((item) =>
          item.id === phaseId
            ? {
              ...item,
              tasks: item.tasks.map((taskItem) =>
                taskItem.id === taskId
                  ? { ...taskItem, status: task.status }
                  : taskItem
              ),
            }
            : item
        )
      );
      showPopup(
        "No se pudo actualizar el estado de la tarea. Intenta nuevamente.",
        true
      );
      return false;
    }
  };

  const handleEditTask = (task, isFromKanban = false) => {
    if (!canEditProject) return;
    const latestTaskData = findTaskWithLatestData(task?.id);
    const mergedTask =
      latestTaskData && task
        ? {
          ...task,
          ...latestTaskData,
          deadline:
            latestTaskData.deadline ||
            latestTaskData.dueDate ||
            task.deadline ||
            task.dueDate,
          dueDate:
            latestTaskData.dueDate ||
            latestTaskData.deadline ||
            task.dueDate ||
            task.deadline,
        }
        : latestTaskData || task;
    setIsPhaseEdit(false);
    setIsModalFromKanban(isFromKanban);
    setTaskToEdit(mergedTask);
    setEditModalOpen(true);
  };

  const handleEditPhase = (phase, isFromKanban = false) => {
    if (!canEditProject) return;
    const tasksForPhase = getPhaseTasksWithLatestData(phase.id);
    setIsPhaseEdit(true);
    setIsModalFromKanban(isFromKanban);
    setTaskToEdit({
      ...phase,
      tasks: tasksForPhase,
    });
    setEditModalOpen(true);
  };

  const handleTaskUpdated = (updatedTaskOrTasks) => {
    const updatesArray = Array.isArray(updatedTaskOrTasks)
      ? updatedTaskOrTasks
      : [updatedTaskOrTasks];

    if (!updatesArray.length) {
      return;
    }

    const updatesMap = new Map(
      updatesArray.filter((item) => item?.id).map((item) => [item.id, item])
    );

    if (!updatesMap.size) {
      return;
    }

    setPhases((prev) =>
      prev.map((phase) => ({
        ...phase,
        tasks: phase.tasks?.map((task) => {
          const update = updatesMap.get(task.id);
          return update ? { ...task, ...update } : task;
        }),
      }))
    );

    if (showPhaseKanban && selectedPhaseId) {
      setLocalPhaseTasks((prevTasks) =>
        prevTasks.map((task) => {
          const update = updatesMap.get(task.id);
          if (!update) {
            return task;
          }

          return {
            ...task,
            status:
              update.status !== undefined
                ? mapTaskStatusToKanban(update.status)
                : task.status,
            title: update.title || update.name || task.title,
            priority: update.priority || task.priority,
            assignee:
              update.assigned_to_name || update.assignee || task.assignee,
            assigned_to_name: update.assigned_to_name || task.assigned_to_name,
            assigned_to:
              update.assigned_to !== undefined
                ? update.assigned_to
                : task.assigned_to,
            dueDate:
              update.dueDate ||
              update.deadline ||
              task.dueDate ||
              task.deadline,
            description:
              update.description !== undefined
                ? update.description
                : task.description,
            documentation_urls: update.documentation_urls ?? task.documentation_urls,
            google_calendar_link:
              update.google_calendar_link !== undefined
                ? update.google_calendar_link
                : task.google_calendar_link,
          };
        })
      );
    }
  };

  const handleCloseModal = () => {
    setEditModalOpen(false);
    setTaskToEdit(null);
    setIsPhaseEdit(false);
    setIsModalFromKanban(false);
  };

  if (projectError) {
    return (
      <EmptyStateContainer
        title="No se pudo cargar el proyecto"
        message="Verifica que tienes acceso al proyecto o intenta nuevamente."
        badgeText="Error"
      />
    );
  }

  if (isLoading && phases.length === 0) {
    return (
      <Stack spacing={3}>
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Skeleton variant="text" width="35%" height={30} />
            <Skeleton variant="text" width="50%" height={20} sx={{ mt: 1 }} />
            <Skeleton variant="rounded" height={10} sx={{ mt: 3 }} />
          </CardContent>
        </Card>
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Skeleton variant="text" width="35%" height={24} />
            <Skeleton variant="rounded" height={80} sx={{ mt: 2 }} />
            <Skeleton variant="rounded" height={80} sx={{ mt: 2 }} />
          </CardContent>
        </Card>
      </Stack>
    );
  }

  if (!projectData) {
    return null;
  }

  if (!phases || phases.length === 0) {
    return (
      <EmptyStateContainer
        title="Este proyecto no tiene fases asociadas"
        message="Añade fases desde la plantilla para comenzar a trabajar."
        badgeText="Sin fases"
      />
    );
  }

  return (
    <Box>
      {showKanban ? (
        <KanbanView
          project={kanbanProjectInfo}
          tasks={getPhasesForKanban()}
          currentUser={currentUser}
          onClose={handleCloseKanban}
          isPhaseView
          readOnly={isReadOnlyClient}
          onDragStart={handlePhaseDragStart}
          onDragOver={handleDragOver}
          onDrop={handlePhaseDrop}
        />
      ) : showPhaseKanban ? (
        <KanbanView
          project={kanbanProjectInfo}
          tasks={getPhaseTasksForKanban()}
          currentUser={currentUser}
          onClose={handleClosePhaseKanban}
          isPhaseTaskView
          readOnly={isReadOnlyClient}
          onTaskStatusUpdate={updatePhaseTaskStatusLocally}
          phaseName={selectedPhase?.title}
          phaseNumber={selectedPhase?.order}
          onDragStart={handlePhaseTaskDragStart}
          onDragOver={handlePhaseTaskDragOver}
          onDrop={handlePhaseTaskDrop}
          onEditTask={(task) => handleEditTask(task, true)}
          isOnlyTasksMode={isOnlyTasksMode}
        />
      ) : (
        <SetupDetailView
          phases={phases}
          projectProgress={projectProgress}
          setupState={projectState || undefined}
          startDate={projectData?.start_date}
          isClient={!canEditTasks}
          onViewPhaseKanban={handleViewPhaseKanban}
          onUpdatePhaseStatus={isReadOnlyClient ? undefined : updatePhaseStatus}
          onUpdateTaskStatus={isReadOnlyClient ? undefined : updateTaskStatus}
          onEditTask={canEditProject ? (task) => handleEditTask(task, false) : undefined}
          onEditPhase={canEditProject ? (phase) => handleEditPhase(phase) : undefined}
        />
      )}

      <EditTaskModal
        open={editModalOpen}
        onClose={handleCloseModal}
        task={taskToEdit}
        consultants={availableConsultants}
        onTaskUpdated={handleTaskUpdated}
        onSuccessPopup={(message) => showPopup(message)}
        onErrorPopup={(message) => showPopup(message, true)}
        isFromKanban={isModalFromKanban}
        isPhaseEdit={isPhaseEdit}
      />

      <ConfirmModal
        open={stateConfirmOpen}
        onClose={() => {
          setStateConfirmOpen(false);
          setPendingStateChange(null);
        }}
        onConfirm={handleConfirmProjectStateChange}
        title="Marcar proyecto como completado"
        description="¿Seguro que quieres marcar este proyecto como completado?"
        confirmLabel="Confirmar"
      />

      <SuccessPopup
        isOpen={popupOpen}
        message={popupMessage}
        error={isError}
        onClose={() => setPopupOpen(false)}
        highPriority={showKanban || showPhaseKanban}
      />
    </Box>
  );
}
